/**
 * VÉRAULT TRAVELS - COMMAND CENTER COMPONENT
 * 
 * "Today's Action Items" = Global OS Command Center (Controller + Navigator)
 * 
 * CRITICAL WINDOW RULE (LOCKED):
 * - Includes anything due TODAY
 * - Includes anything due within next 30 days to keep trips travel-ready
 * 
 * Sources:
 * - Passport validity deadlines (9-month, 6-month alerts)
 * - Airline check-in windows (48h/24h reminders)
 * - Intake not sent / not completed
 * - Approval pending / republish decisions
 * - Payments due / authorization missing
 * - Booking deadlines / cancellation windows
 * - Missing logistics confirmations
 * - Unreplied communications flagged "awaiting reply"
 * - Trip blockers computed by system
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, AlertCircle, Check, ChevronRight, Clock, Calendar,
  Send, RefreshCw, Eye, EyeOff, X, Bell, FileText, DollarSign,
  Plane, Users, Shield, Loader2, Filter, ArrowUpDown, CheckCircle2,
  ExternalLink, MoreHorizontal, Inbox
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Priority colors
const PRIORITY_STYLES = {
  critical: { bg: 'bg-red-50', border: 'border-l-4 border-red-500', dot: 'bg-red-500', text: 'text-red-700' },
  high: { bg: 'bg-amber-50', border: 'border-l-4 border-amber-500', dot: 'bg-amber-500', text: 'text-amber-700' },
  medium: { bg: 'bg-blue-50', border: 'border-l-4 border-blue-500', dot: 'bg-blue-500', text: 'text-blue-700' },
  low: { bg: 'bg-gray-50', border: 'border-l-4 border-gray-300', dot: 'bg-gray-400', text: 'text-gray-600' }
};

// Action type icons
const ACTION_ICONS = {
  passport: Shield,
  checkin: Plane,
  intake: FileText,
  approval: Eye,
  payment: DollarSign,
  booking: Calendar,
  logistics: Plane,
  communication: Inbox,
  blocker: AlertTriangle
};

// ============================================================
// ACTION ITEM CARD
// ============================================================
function ActionItemCard({ item, onQuickAction, onNavigate, loading }) {
  const [expanded, setExpanded] = useState(false);
  const priority = PRIORITY_STYLES[item.priority] || PRIORITY_STYLES.medium;
  const Icon = ACTION_ICONS[item.type] || AlertCircle;
  
  const handleClick = () => {
    if (item.deep_link) {
      onNavigate(item.deep_link);
    }
  };
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={`rounded-lg ${priority.bg} ${priority.border} transition-all hover:shadow-md`}
      data-testid={`action-item-${item.id}`}
    >
      <div className="p-3">
        <div className="flex items-start gap-3">
          {/* Priority Indicator */}
          <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${priority.dot}`} />
          
          {/* Icon */}
          <div className={`w-8 h-8 rounded-lg bg-white/60 flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-4 h-4 ${priority.text}`} />
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <button
              onClick={handleClick}
              className="w-full text-left group"
            >
              <h4 className="text-sm font-medium text-[#1A1A1A] group-hover:text-[#C8A85A] transition-colors">
                {item.title}
              </h4>
              <p className="text-xs text-[#8C8C8C] mt-0.5 line-clamp-2">{item.message}</p>
              
              {/* Meta info */}
              <div className="flex items-center gap-3 mt-2 text-xs text-[#8C8C8C]">
                {item.trip_name && (
                  <span className="flex items-center gap-1">
                    <Plane className="w-3 h-3" />
                    {item.trip_name}
                  </span>
                )}
                {item.due_date && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {item.due_date}
                  </span>
                )}
                {item.days_until_due !== undefined && item.days_until_due <= 7 && (
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                    item.days_until_due <= 0 ? 'bg-red-100 text-red-700' :
                    item.days_until_due <= 3 ? 'bg-amber-100 text-amber-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {item.days_until_due <= 0 ? 'Today!' : `${item.days_until_due}d left`}
                  </span>
                )}
              </div>
            </button>
          </div>
          
          {/* Quick Actions */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {item.quick_actions?.map((action, idx) => (
              <Button
                key={idx}
                size="sm"
                variant={action.primary ? 'default' : 'outline'}
                onClick={(e) => {
                  e.stopPropagation();
                  onQuickAction(item.id, action.action_type);
                }}
                disabled={loading === item.id}
                className={`h-7 text-xs ${action.primary ? 'bg-[#C8A85A] hover:bg-[#B39049]' : ''}`}
                title={action.label}
              >
                {loading === item.id ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  action.icon === 'send' ? <Send className="w-3 h-3" /> :
                  action.icon === 'check' ? <Check className="w-3 h-3" /> :
                  action.icon === 'eye' ? <Eye className="w-3 h-3" /> :
                  action.label
                )}
              </Button>
            ))}
            
            <button
              onClick={handleClick}
              className="p-1.5 rounded hover:bg-white/60 transition-colors"
            >
              <ChevronRight className="w-4 h-4 text-[#8C8C8C]" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ============================================================
// HEALTH SUMMARY STRIP
// ============================================================
export function HealthSummaryStrip({ stats, onFilterClick }) {
  const items = [
    { key: 'blockers', label: 'Blockers', count: stats.blockers, color: 'text-red-600', bg: 'bg-red-50' },
    { key: 'waiting_client', label: 'Waiting on Client', count: stats.waiting_on_client, color: 'text-amber-600', bg: 'bg-amber-50' },
    { key: 'waiting_advisor', label: 'Waiting on You', count: stats.waiting_on_advisor, color: 'text-blue-600', bg: 'bg-blue-50' },
    { key: 'departures_7', label: 'Departures (7d)', count: stats.departures_next_7_days, color: 'text-purple-600', bg: 'bg-purple-50' },
    { key: 'departures_30', label: 'Departures (30d)', count: stats.departures_next_30_days, color: 'text-teal-600', bg: 'bg-teal-50' },
    { key: 'in_travel', label: 'In Travel', count: stats.trips_in_travel, color: 'text-green-600', bg: 'bg-green-50' },
  ];
  
  return (
    <div 
      className="flex flex-wrap gap-2 p-3 bg-white rounded-lg border border-[#E6E4DE]"
      data-testid="health-summary-strip"
    >
      {items.map(item => (
        <button
          key={item.key}
          onClick={() => onFilterClick(item.key)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${item.bg} hover:opacity-80 transition-opacity`}
          data-testid={`health-${item.key}`}
        >
          <span className={`text-lg font-semibold ${item.color}`}>{item.count || 0}</span>
          <span className={`text-xs ${item.color}`}>{item.label}</span>
        </button>
      ))}
    </div>
  );
}

// ============================================================
// MAIN COMMAND CENTER
// ============================================================
export function VTCommandCenter({ onNavigate, onRefresh }) {
  const navigate = useNavigate();
  const [actionItems, setActionItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickActionLoading, setQuickActionLoading] = useState(null);
  const [sortBy, setSortBy] = useState('urgency'); // urgency, departure, waiting_on
  const [filterBy, setFilterBy] = useState('all'); // all, blockers, waiting_client, waiting_advisor
  const [showOnlyBlockers, setShowOnlyBlockers] = useState(false);
  const [healthStats, setHealthStats] = useState({});
  
  // Fetch action items (30-day window)
  const fetchActionItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/advisor/action-items`, {
        params: { window_days: 30 }
      });
      setActionItems(res.data.items || []);
      setHealthStats(res.data.stats || {});
    } catch (e) {
      console.error('Failed to fetch action items:', e);
      // Fallback to alerts API
      try {
        const alertsRes = await axios.get(`${API}/advisor/alerts`);
        setActionItems(alertsRes.data.alerts || []);
      } catch (e2) {
        console.error('Failed to fetch alerts:', e2);
      }
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchActionItems();
  }, [fetchActionItems]);
  
  // Handle quick action
  const handleQuickAction = async (itemId, actionType) => {
    setQuickActionLoading(itemId);
    try {
      const item = actionItems.find(i => i.id === itemId);
      
      switch (actionType) {
        case 'send_intake':
          await axios.post(`${API}/travels/trips/${item.trip_id}/intake/send`);
          toast.success('Intake sent');
          break;
        case 'mark_not_needed':
          await axios.patch(`${API}/travels/trips/${item.trip_id}/intake`, { not_needed: true });
          toast.success('Intake marked as not needed');
          break;
        case 'nudge_approval':
          await axios.post(`${API}/travels/trips/${item.trip_id}/nudge-approval`);
          toast.success('Approval reminder sent');
          break;
        case 'mark_checkin_confirmed':
          await axios.patch(`${API}/travels/trips/${item.trip_id}/checkin-confirmed`);
          toast.success('Check-in confirmed');
          break;
        case 'acknowledge':
          await axios.patch(`${API}/advisor/alerts/${itemId}`, { acknowledged: true });
          toast.success('Acknowledged');
          break;
        case 'snooze':
          await axios.patch(`${API}/advisor/alerts/${itemId}`, { snoozed_until: new Date(Date.now() + 24*60*60*1000).toISOString() });
          toast.success('Snoozed for 24h');
          break;
        default:
          console.log('Unknown action:', actionType);
      }
      
      // Refresh after action
      await fetchActionItems();
      onRefresh?.();
      
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setQuickActionLoading(null);
    }
  };
  
  // Handle navigation
  const handleNavigate = (deepLink) => {
    if (deepLink.startsWith('/')) {
      navigate(deepLink);
    } else if (deepLink.startsWith('http')) {
      window.location.href = deepLink;
    } else {
      // Parse trip-specific deep links
      const [tripId, tab, section] = deepLink.split('/');
      if (tripId) {
        const url = `/advisor/trips/${tripId}${tab ? `?tab=${tab}` : ''}${section ? `&section=${section}` : ''}`;
        window.location.href = url;
      }
    }
  };
  
  // Filter and sort items
  const filteredItems = useMemo(() => {
    let items = [...actionItems];
    
    // Filter
    if (showOnlyBlockers) {
      items = items.filter(i => i.is_blocker);
    } else if (filterBy !== 'all') {
      switch (filterBy) {
        case 'blockers':
          items = items.filter(i => i.is_blocker);
          break;
        case 'waiting_client':
          items = items.filter(i => i.waiting_on === 'client');
          break;
        case 'waiting_advisor':
          items = items.filter(i => i.waiting_on === 'advisor');
          break;
      }
    }
    
    // Sort
    items.sort((a, b) => {
      if (sortBy === 'urgency') {
        const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
        return (priorityOrder[a.priority] || 3) - (priorityOrder[b.priority] || 3);
      } else if (sortBy === 'departure') {
        return new Date(a.departure_date || '9999-12-31') - new Date(b.departure_date || '9999-12-31');
      } else if (sortBy === 'waiting_on') {
        const waitOrder = { advisor: 0, client: 1, system: 2 };
        return (waitOrder[a.waiting_on] || 2) - (waitOrder[b.waiting_on] || 2);
      }
      return 0;
    });
    
    return items;
  }, [actionItems, filterBy, sortBy, showOnlyBlockers]);
  
  // Handle health filter click
  const handleHealthFilterClick = (key) => {
    if (key === 'blockers') {
      setShowOnlyBlockers(!showOnlyBlockers);
      setFilterBy('all');
    } else if (key.startsWith('waiting_')) {
      setFilterBy(filterBy === key ? 'all' : key);
      setShowOnlyBlockers(false);
    } else if (key.startsWith('departures_')) {
      setSortBy('departure');
    }
  };
  
  return (
    <div className="space-y-4" data-testid="vt-command-center">
      {/* Health Summary Strip */}
      <HealthSummaryStrip stats={healthStats} onFilterClick={handleHealthFilterClick} />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-[#C8A85A]" />
          <h2 className="font-serif text-xl text-[#1A1A1A]">Today&apos;s Action Items</h2>
          {filteredItems.length > 0 && (
            <span className="px-2 py-0.5 text-xs rounded-full bg-[#C8A85A]/10 text-[#C8A85A]">
              {filteredItems.length}
            </span>
          )}
        </div>
        
        {/* Filters */}
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={showOnlyBlockers ? 'default' : 'outline'}
            onClick={() => {
              setShowOnlyBlockers(!showOnlyBlockers);
              setFilterBy('all');
            }}
            className={showOnlyBlockers ? 'bg-red-500 hover:bg-red-600' : ''}
          >
            <AlertTriangle className="w-3 h-3 mr-1" />
            Blockers
          </Button>
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="h-8 px-2 text-xs border border-[#E6E4DE] rounded bg-white"
          >
            <option value="urgency">Sort: Urgency</option>
            <option value="departure">Sort: Departure Date</option>
            <option value="waiting_on">Sort: Waiting On</option>
          </select>
          
          <Button size="sm" variant="ghost" onClick={fetchActionItems}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>
      
      {/* 30-day window indicator */}
      <p className="text-xs text-[#8C8C8C]">
        Showing items due today and within the next 30 days for travel readiness
      </p>
      
      {/* Action Items List */}
      <div className="space-y-2 max-h-[60vh] overflow-y-auto">
        <AnimatePresence>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-[#C8A85A]" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle2 className="w-12 h-12 text-[#8DA399]/40 mx-auto mb-3" />
              <p className="text-[#8C8C8C]">
                {showOnlyBlockers ? 'No blockers - all trips are travel-ready!' : 'All caught up!'}
              </p>
            </div>
          ) : (
            filteredItems.map(item => (
              <ActionItemCard
                key={item.id}
                item={item}
                onQuickAction={handleQuickAction}
                onNavigate={handleNavigate}
                loading={quickActionLoading}
              />
            ))
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default VTCommandCenter;
